import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Package, LogIn, ChevronRight } from "lucide-react";
import { useCustomerAuth } from "../shop/AuthContext";
import ShopAuthCallback from "../shop/AuthCallback";
import { fetchMyOrders } from "../shop/api";
import { useSEO } from "../lib/seo";

const STATUS_STYLES = {
  paid: "text-emerald-400 border-emerald-400/30 bg-emerald-400/10",
  pending: "text-amber-300 border-amber-300/30 bg-amber-300/10",
  failed: "text-red-400 border-red-400/30 bg-red-400/10",
  cancelled: "text-mute border-line bg-white/[0.03]",
  shipped: "text-sky-300 border-sky-300/30 bg-sky-300/10",
  delivered: "text-gold border-gold/30 bg-gold/10",
};

export default function MyOrdersPage() {
  useSEO({
    title: "My Orders — GenieX Shop",
    description: "View your GenieX order history.",
    path: "/account/orders",
  });

  const auth = useCustomerAuth();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  const hashHasSession = (window.location.hash || "").includes("session_id=");

  useEffect(() => {
    if (!auth.user) {
      setLoading(false);
      return;
    }
    fetchMyOrders()
      .then((d) => setOrders(d.orders || []))
      .finally(() => setLoading(false));
  }, [auth.user]);

  if (hashHasSession) {
    return <ShopAuthCallback redirectTo="/account/orders" />;
  }

  if (auth.loading) {
    return (
      <main className="min-h-screen bg-[var(--gx-bg)] pt-40 text-center text-dim" data-testid="my-orders-loading">
        Loading…
      </main>
    );
  }

  if (!auth.user) {
    return (
      <main className="min-h-screen bg-[var(--gx-bg)] text-white pt-32 lg:pt-40 pb-24 text-center" data-testid="my-orders-signin-required">
        <LogIn className="w-8 h-8 text-gold mx-auto mb-5" />
        <h1 className="h2-display mb-3">Sign in to view your orders</h1>
        <p className="text-dim max-w-[42ch] mx-auto mb-8">
          We use your Google account to securely show your GenieX order history.
        </p>
        <button
          type="button"
          onClick={() => auth.loginWithGoogle("/account/orders")}
          data-testid="my-orders-google-signin"
          className="btn-gold mx-auto"
        >
          Continue with Google
        </button>
      </main>
    );
  }

  return (
    <main data-testid="my-orders-page" className="bg-[var(--gx-bg)] text-white pt-28 lg:pt-36 pb-24 min-h-screen">
      <div className="max-w-[760px] mx-auto px-5 lg:px-0">
        <p className="eyebrow mb-3">My Orders</p>
        <h1 className="h2-display mb-10">Order history</h1>

        {loading ? (
          <p className="text-dim" data-testid="my-orders-list-loading">
            Loading your orders…
          </p>
        ) : orders.length === 0 ? (
          <div className="text-center py-16 border border-line rounded-2xl" data-testid="my-orders-empty">
            <Package className="w-8 h-8 text-mute mx-auto mb-4" />
            <p className="text-dim mb-6">You haven&apos;t placed any orders yet.</p>
            <Link to="/shop" data-testid="my-orders-shop-link" className="btn-outline mx-auto inline-flex">
              Browse the Shop
            </Link>
          </div>
        ) : (
          <div className="space-y-4" data-testid="my-orders-list">
            {orders.map((order) => (
              <Link
                key={order.id}
                to={`/checkout/confirmation/${order.id}`}
                data-testid={`my-order-${order.id}`}
                className="group flex items-center gap-4 border border-line rounded-2xl p-5 hover:border-gold/40 transition-colors"
              >
                <div className="w-11 h-11 rounded-xl border border-line flex items-center justify-center text-gold shrink-0">
                  <Package className="w-5 h-5" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-3 flex-wrap">
                    <span className="font-medium">{order.order_number}</span>
                    <span
                      className={`text-[10.5px] tracking-[0.18em] uppercase px-2 py-0.5 rounded-full border ${
                        STATUS_STYLES[order.status] || STATUS_STYLES.pending
                      }`}
                      data-testid={`my-order-status-${order.id}`}
                    >
                      {order.status}
                    </span>
                  </div>
                  <p className="text-dim text-sm mt-1 truncate">
                    {order.items?.map((it) => it.name).join(", ")}
                  </p>
                  <p className="text-mute text-[11px] mt-1">
                    {new Date(order.created_at).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}
                  </p>
                </div>
                <div className="text-right shrink-0">
                  <p className="text-gold font-display text-lg">₹{Number(order.total).toLocaleString("en-IN")}</p>
                </div>
                <ChevronRight className="w-4 h-4 text-mute group-hover:text-gold transition-colors shrink-0" />
              </Link>
            ))}
          </div>
        )}
      </div>
    </main>
  );
}
